// MST
import { types } from 'mobx-state-tree'

// Interfaces
import { ISharedFileUserWithIdStatus, ISharedQuestionSetResultDetail } from 'interfaces/shared'
import {
  ISharedCommonFileAnswerAttrs,
  ISharedCommonLoading,
  ISharedCommonLoadingAttrs,
  ISharedCommonLoadingQuestionSetAttrs,
  ISharedCommonMapFile,
  ISharedCommonMapFileAttrs,
  ISharedCommonUpdateAnswerAttrs,
} from 'interfaces/shared/shared-common.interface'

// Constant
import { ASSESSMENT_FEATURES, COLUMN_TYPE, LOADING_KEY_TYPE, QUESTION_TYPES } from 'constant/shared.constant'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'

// UUID
import { v4 as uuidv4 } from 'uuid'
import { IGeneralProof } from 'interfaces/webpage/certification/registration/precondition'

// Types
const { optional, model, boolean } = types

// Question Types
const { CHECKBOX, DATE, PARAGRAPH, SHORT_ANSWER, MULTIPLE_CHOICE } = QUESTION_TYPES

// Initial State
const loadingInitialState: ISharedCommonLoading = {
  isActionLoading: false,
  isDetailLoading: false,
  isDownloadLoading: false,
  isLoading: false,
  isDropdownLoading: false,
}

export const withHelper_commonLoadingModel = optional(
  model('Loading').props({
    isLoading: optional(boolean, false),
    isDetailLoading: optional(boolean, false),
    isActionLoading: optional(boolean, false),
    isDownloadLoading: optional(boolean, false),
    isDropdownLoading: optional(boolean, false),
  }),
  {},
)

/**
 * @description Map loading
 *
 * @param {ISharedCommonLoading} loading
 * @param {ISharedCommonLoadingAttrs} payload
 *
 * @return {ISharedCommonLoading} loading
 */
export const withHelper_commonMapLoading = (
  loading: ISharedCommonLoading,
  { type, value }: ISharedCommonLoadingAttrs,
): ISharedCommonLoading => {
  return cloneDeep({ ...loading, [type]: value })
}

/**
 * @description Get previous value
 *
 * @return {string} string
 */
export const withHelper_commonGetPreviousAnswerValue = ({ answer }: ISharedQuestionSetResultDetail): string => {
  switch (answer?.answerType) {
    case MULTIPLE_CHOICE:
      return answer.answerOption.find(answerOption => answerOption.isSelected)?.value || '-'
    default:
      return answer?.answerValue
  }
}

/**
 * @description Map user files
 *
 * @param {ISharedCommonMapFile[]} files
 * @param {object} type
 *
 * @return {ISharedCommonMapFile[]} array of user files
 */
export const withHelper_commonMapUserFiles = (
  files: ISharedCommonMapFile[],
  { fileId, type, value }: ISharedCommonMapFileAttrs,
): ISharedCommonMapFile[] => {
  // Check if user want to add file item
  if (type === 'add') {
    const newFile: ISharedCommonMapFile = {
      id: uuidv4(),
      userFileBlobId: '',
      userFileTransactionId: null,
      userOriginalFileName: '',
      loading: loadingInitialState,
      status: null,
    }

    return [...files, newFile]
  }

  // Check if user want to edit
  if (type === 'edit') {
    return files.map(file => {
      if (file.id === fileId) {
        return { ...file, ...value } // value = ISharedFileUserWithIdStatus interface
      } else {
        return file
      }
    })
  }

  // Check if user want to delete but element but element will stay
  if (type === 'delete' && fileId) {
    return files.map(file => {
      if (file.id === fileId) {
        return {
          ...file,
          userFileBlobId: '',
          userFileTransactionId: null,
          userOriginalFileName: '',
        }
      } else {
        return file
      }
    })
  }

  // Check if user want to delete file item
  if (type === 'deleteItem' && fileId) {
    return files.filter(file => file.id !== fileId)
  }

  // Don't map anything
  return files
}

/**
 * @description Map
 */

/**
 * @description Handle Question Set Loading
 *
 * @param {ISharedCommonLoadingQuestionSetAttrs} payload
 * @param {boolean} value
 *
 * @return {void} void
 */
export const withHelper_commonSetQuestionSetLoading = (
  payload: ISharedCommonLoadingQuestionSetAttrs,
  value: boolean,
): ISharedCommonLoadingQuestionSetAttrs => {
  return {
    ...payload,
    value,
  }
}

/**
 * @description Map question set
 *
 * @param {IGeneralProof} questionSets
 *
 * @return {IGeneralProof[]} array of question sets
 */
export const withHelper_commonMapQuestionSet = (questionSets: IGeneralProof[]): IGeneralProof[] => {
  return cloneDeep(
    questionSets.map(questionSet => {
      return {
        ...questionSet,
        groupData: questionSet?.groupData?.map(item => ({
          ...item,
          result: item.result?.map(result => {
            return {
              ...result,
  
              // Assign UUID for identifier in Front-End
              id: uuidv4(),
              resultDetail: result?.resultDetail?.map(resultDetail => {
                return {
                  ...resultDetail,
  
                  // Assign UUID for identifier in Front-End
                  id: uuidv4(),
  
                  answer:
                    resultDetail?.answer !== null
                      ? {
                          ...resultDetail?.answer,
                          answerFiles: !isEmpty(resultDetail.answer?.answerFiles)
                            ? resultDetail.answer?.answerFiles.map(answerFile => ({
                                id: uuidv4(),
                                ...answerFile,
                              }))
                            : [
                                {
                                  id: uuidv4(),
                                  userFileBlobId: '',
                                  userFileTransactionId: null,
                                  userOriginalFileName: '',
                                  status: null,
                                  loading: loadingInitialState,
                                },
                              ],
                        }
                      : null,
                }
              }),
            }
          })
        }))
      }
    }),
  )
}

/**
 * @description Map answer loading
 *
 * @param {IGeneralProof[]} questionSets
 * @param {ISharedCommonLoadingQuestionSetAttrs} loading
 *
 * @return {IGeneralProof[]} array of question sets
 */
export const withHelper_commonMapQuestionSetLoading = (
  questionSets: IGeneralProof[],
  { questionSetId, resultId, resultDetailId, fileId, type, loadingType, value }: ISharedCommonLoadingQuestionSetAttrs,
): IGeneralProof[] => {
  return cloneDeep(
    questionSets.map(questionSet => {
      // Check if questionSetId is match
      if (questionSetId === questionSet.questionSetId) {
        return {
          ...questionSet,
          groupData: questionSet.groupData.map(item => ({
            ...item,
            result: item.result.map(questionSetResult => {
              // Match the result
              if (questionSetResult.id === resultId) {
                return {
                  ...questionSetResult,
                  resultDetail: questionSetResult.resultDetail.map(resultDetail => {
                    // Match the result detail
                    // Check if type only answer and answer not null
                    if (
                      resultDetail.type === COLUMN_TYPE.ANSWER &&
                      resultDetail.answer !== null &&
                      resultDetail.id === resultDetailId
                    ) {
                      return {
                        ...resultDetail,
                        answer: {
                          ...resultDetail.answer,
                          // Check if user want to start loading for files
                          answerFiles:
                            type === LOADING_KEY_TYPE.ANSWER_FILES
                              ? resultDetail.answer.answerFiles.map(answerFile => {
                                  if (answerFile.id === fileId) {
                                    return {
                                      ...answerFile,
                                      loading: withHelper_commonMapLoading(answerFile.loading!, { type: loadingType, value }),
                                    }
                                  } else {
                                    return answerFile
                                  }
                                })
                              : resultDetail.answer.answerFiles,
  
                          // Check if user want to start loading for common answer (outside of files)
                          loading:
                            type === LOADING_KEY_TYPE.ANSWER
                              ? withHelper_commonMapLoading(resultDetail.answer?.loading!, { type: loadingType, value })
                              : resultDetail.answer.loading,
                        },
  
                        // Check if user want to start loading outside answer
                        loading:
                          type === LOADING_KEY_TYPE.RESULT_DETAIL
                            ? withHelper_commonMapLoading(resultDetail.loading!, { type: loadingType, value })
                            : resultDetail.loading,
                      }
                    } else {
                      return resultDetail
                    }
                  }),
                }
              } else {
                return questionSetResult
              }
            }),
          }))
        }
      } else {
        return questionSet
      }
    }),
  )
}

/**
 * @description Get index from question set result detail
 *
 * @param {ISharedQuestionSetResultDetail[]} resultDetail
 *
 * @return {number} number
 */
const withHelper_commonQuestionSetResultDetailAnswerOptionIndex = (
  featureName: ASSESSMENT_FEATURES,
  resultDetail: ISharedQuestionSetResultDetail[],
  value: any,
): number => {
  // Check if feature is general proof
  // Developer should be validate the 'Ada' column in the table
  // 'Ada' column will be in index [1],
  // And answer should be MULTIPLE_CHOICE
  // If that index did not exists, contact the BE / BPA
  if (featureName === ASSESSMENT_FEATURES.GENERAL_PROOF) {
    return resultDetail?.[1]?.answer?.answerOption?.findIndex(answerOption => answerOption.value === value) as number
  }

  return -1
}

/**
 * @description Map answer value
 *
 * @param {IGeneralProof[]} questionSets
 * @param {ISharedCommonUpdateAnswerAttrs} attrs
 *
 * @return {IGeneralProof[]} array of question sets
 */
export const withHelper_commonMapAnswerValue = (
  questionSets: IGeneralProof[],
  {
    questionSetId,
    resultId,
    resultDetailId,
    value,
    featureName,
    callbackResultDetail,
    callbackResultDetailAnswerOptionIndex,
  }: ISharedCommonUpdateAnswerAttrs,
): IGeneralProof[] => {
  return cloneDeep(
    questionSets.map(questionSet => {
      if (questionSet.questionSetId === questionSetId) {
        return {
          ...questionSet,
          groupData: questionSet.groupData.map(item => ({
            ...item,
            result: item.result.map(questionSetResult => {
              // Match the result
              if (questionSetResult.id === resultId) {
                // Throw callback to parent for resultDetail
                callbackResultDetail && callbackResultDetail(questionSetResult.resultDetail)
  
                // Throw callback to parent for resultDetail answer option, but index only
                // Note, this is used in Certificate Registration - General Proof
                callbackResultDetailAnswerOptionIndex &&
                  featureName &&
                  callbackResultDetailAnswerOptionIndex(
                    withHelper_commonQuestionSetResultDetailAnswerOptionIndex(featureName, questionSetResult.resultDetail, value),
                    questionSetResult.resultDetail,
                  )
  
                // And Map
                return {
                  ...questionSetResult,
                  resultDetail: questionSetResult.resultDetail.map(resultDetail => {
                    // Match the result detail
                    // Check if type only answer and answer not null
                    if (
                      resultDetail.type === COLUMN_TYPE.ANSWER &&
                      resultDetail.answer !== null &&
                      resultDetail.id === resultDetailId
                    ) {
                      return {
                        ...resultDetail,
                        answer: {
                          ...resultDetail.answer,
                          answerValue: [DATE, PARAGRAPH, SHORT_ANSWER].includes(resultDetail.answer.answerType!)
                            ? value // any
                            : resultDetail.answer.answerValue,
                          answerOption: [CHECKBOX, MULTIPLE_CHOICE].includes(resultDetail.answer.answerType!)
                            ? resultDetail.answer.answerOption.map(answerOption => {
                                // Todo: For Checkbox
                                return { ...answerOption, isSelected: answerOption.value === value }
                              })
                            : resultDetail.answer.answerOption,
                        },
                      }
                    } else {
                      return resultDetail
                    }
                  }),
                }
              } else {
                return questionSetResult
              }
            }),
          }))
        }
      } else {
        return questionSet
      }
    }),
  )
}

/**
 * @description Map answer files
 *
 * @param {IGeneralProof[]} questionSets
 * @param {ISharedCommonUpdateAnswerAttrs} attrs
 * @param {string} type
 *
 * @return {IGeneralProof[]} array of question sets
 */
export const withHelper_commonMapAnswerFiles = (
  questionSets: IGeneralProof[],
  { questionSetId, resultId, resultDetailId, fileId, type, value }: ISharedCommonFileAnswerAttrs,
): IGeneralProof[] => {
  return cloneDeep(
    questionSets.map(questionSet => {
      if (questionSet.questionSetId === questionSetId) {
        return {
          ...questionSet,
          groupData: questionSet.groupData.map(item => ({
            ...item,
            result: item.result.map(result => {
              if (result.id === resultId) {
                return {
                  ...result,
                  resultDetail: result.resultDetail.map(resultDetail => {
                    if (
                      resultDetail.type === COLUMN_TYPE.ANSWER &&
                      resultDetail.answer !== null &&
                      resultDetail.id === resultDetailId
                    ) {
                      // Check if file is more than 5
                      if (resultDetail.answer.answerOption.length > 5) {
                        return resultDetail
                      } else {
                        return {
                          ...resultDetail,
                          answer: {
                            ...resultDetail.answer,
                            answerFiles: withHelper_commonMapUserFiles(resultDetail.answer.answerFiles, {
                              type,
                              value,
                              fileId,
                            }) as ISharedFileUserWithIdStatus[],
                          },
                        }
                      }
                    } else {
                      return resultDetail
                    }
                  }),
                }
              } else {
                return result
              }
            }),
          }))
        }
      } else {
        return questionSet
      }
    }),
  )
}
